import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export const fetchTypes = createAsyncThunk(
  "type/fetchTypes",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search.trim());
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    const filterPayload = FilterBuilder(filters ?? []);

    const body = {
      filters: filterPayload,
    };
    const response: any = await Axios.post(
      `${API_ENDPOINT?.MASTERS?.TYPE?.LIST}?${params.toString()}`,
      body,
    );
    return {
      data: response.data.data || [],
      rowCount: response.data.count || 0,
    };
  },
);

export const updateStatusInType = createAsyncThunk(
  "category/updateStatusInType",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        `${API_ENDPOINT.MASTERS?.TYPE.STATUS_UPDATE(id)}`,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in type",
      );
    }
  },
);
export const fetchTypeById = createAsyncThunk(
  "type/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.TYPE?.DETAIL(id)}`,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Type by ID",
      );
    }
  },
);

export const fetchTypeByDropDown = createAsyncThunk(
  "type/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/type/dropdown/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch Type list");
    }
  },
);

export const fetchTypesBySubCategory = createAsyncThunk(
  "type/fetchBySubCategory",
  async (subCategoryId: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.MASTERS?.TYPE?.DROPDOWN_BY_SUB_CATEGORY(subCategoryId)}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Types by Sub-Category",
      );
    }
  },
);

export const updateType = createAsyncThunk(
  "type/updateType",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${API_ENDPOINT.MASTERS?.TYPE?.UPDATE(id)}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update Type");
    }
  },
);

export const addType = createAsyncThunk(
  "type/addType",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT.MASTERS?.TYPE?.CREATE}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add Type");
    }
  },
);

export const deleteType = createAsyncThunk(
  "type/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(`${API_ENDPOINT?.MASTERS?.TYPE?.DELETE(id)}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const exportTypeCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT?.MASTERS?.TYPE?.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "type"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
